import {
  string as yupString,
  number as yupNumber,
  object as yupObject,
} from 'yup';
import merge from 'lodash/merge';

import { usCountryTaxTypes, countryCodes, NOLValues, SecondaryTax } from './constants';
import { YES_FLAG, NO_FLAG, TAX_CURRENCIES } from '../../shared/constants';
import { validateBaseSchema } from '../../shared/gridEdit/validators';
import { areEqualObligations } from './helpers';


const ERROR_MESSAGES = {
  required: fieldName => () => `${fieldName} is required`,
  oneOf: fieldName => ({ values }) => `${fieldName} must be one of ${values}`,
};

export const obligationSchemaShape = {
  enabledFlag: yupString().when(['companyCode', '$companyCodeOptions'], {
    is: (companyCode, companyCodeOptions) => {
      const foundOption = companyCodeOptions.find(option => option.value === companyCode);
      return foundOption && foundOption.enabledFlag === NO_FLAG;
    },
    then: yupString()
      .oneOf([NO_FLAG], 'Enabled Flag must be \'N\' when company code is not enabled')
      .required(ERROR_MESSAGES.required('Enabled Flag')),
    otherwise: yupString()
      .oneOf([NO_FLAG, YES_FLAG], 'Enabled Flag must be \'Y\' or \'N\'')
      .required(ERROR_MESSAGES.required('Enabled Flag')),
  }),
  companyCode: yupString().required(ERROR_MESSAGES.required('Company Code')),
  legalEntity: yupString().required(ERROR_MESSAGES.required('Legal Entity')),
  countryCode: yupString().required(ERROR_MESSAGES.required('Country Code')),
  countryTaxType: yupString().required(ERROR_MESSAGES.required('Country Tax Type')),
  countryTaxTypeDescription: yupString().required(ERROR_MESSAGES.required('Country Tax Type Description')),
  chartName: yupString().when('enabledFlag', {
    is: enabledFlag => enabledFlag === YES_FLAG,
    then: yupString().nullable().required(ERROR_MESSAGES.required('Chart Name')),
    otherwise: yupString().nullable(),
  }),
  connectionName: yupString().nullable(),
  usTaxType: yupString().required(ERROR_MESSAGES.required('US Tax Type')),
  localTaxType: yupString().when('countryCode', {
    is: countryCode => countryCode !== countryCodes.US,
    then: yupString().nullable().required(ERROR_MESSAGES.required('Local Tax Type')),
    otherwise: yupString().nullable(),
  }),
  functionalCurrency: yupString().required(ERROR_MESSAGES.required('Functional Currency')),
  statCurrency: yupString().when(['companyCode', '$companyCodeOptions'], {
    is: (companyCode, companyCodeOptions) => {
      const foundOption = companyCodeOptions.find(option => option.value === companyCode);
      return foundOption && foundOption.statCalc === YES_FLAG;
    },
    then: yupString().nullable().required(ERROR_MESSAGES.required('STAT Currency')),
    otherwise: yupString().nullable(),
  }),
  taxCurrency: yupString().when('countryCode', {
    is: countryCode => countryCode === countryCodes.US,
    then: yupString().oneOf([TAX_CURRENCIES.USD], 'Tax Currency must be \'USD\''),
    otherwise: yupString().required(ERROR_MESSAGES.required('Tax Currency')),
  }),
  filingGroup: yupString().nullable(),
  secondaryTax: yupString().nullable(),
  homeOfficeCompanyCode: yupString().when(['secondaryTax', 'countryCode', 'companyCode', '$companyCodeOptions'], {
    is: (secondaryTax, countryCode, companyCode, companyCodeOptions) => {
      const foundOption = companyCodeOptions.find(option => option.value === companyCode);
      return secondaryTax === 'Branch' && foundOption
        && foundOption.associatedLeCountryCode === countryCode
        && countryCode !== countryCodes.US;
    },
    then: yupString().when(['enabledFlag'], {
      is: enabledFlag => enabledFlag === YES_FLAG,
      then: yupString().required(ERROR_MESSAGES.required('Home Office Company Code'))
        .when(['homeOfficeCompanyCode', 'connectionName', '$companyCodeOptions'], {
          is: (homeOfficeCompanyCode, connectionName, companyCodeOptions) => {
            const foundCompanyCode = companyCodeOptions.find(option => option.value === homeOfficeCompanyCode);
            return foundCompanyCode && foundCompanyCode.connName === connectionName;
          },
          then: yupString(),
          otherwise: yupString().nullable().oneOf(['', null], 'Home Office Company Code has to be in the same connection as the obligation'),
        }),
      otherwise: yupString().nullable(),
    }),
    otherwise: yupString().nullable().oneOf(['', null], 'Home Office Company Code must be empty'),
  }),
  recordFTC: yupString().when(['countryCode', 'companyCode', '$companyCodeOptions'], {
    is: (countryCode, companyCode, companyCodeOptions) => {
      const foundOption = companyCodeOptions.find(option => option.value === companyCode);
      return foundOption
        && foundOption.associatedLeCountryCode === countryCode
        && countryCode !== countryCodes.US;
    },
    then: yupString().when('secondaryTax', {
      is: secondaryTax => Boolean(secondaryTax),
      then: yupString().nullable(),
      otherwise: yupString().nullable().oneOf([NO_FLAG], 'Record FTC must be \'N\''),
    }),
    otherwise: yupString().nullable(),
  }),
  federalFTCLimit: yupString().nullable(),
  stateMinimumTax: yupNumber().nullable(),
  stateApportionmentRate: yupNumber().nullable(),
  statePrePostNOL: yupString().when(['countryCode', 'countryTaxType', 'NOL'], {
    is: (countryCode, countryTaxType, NOL) =>
      countryCode === countryCodes.US && countryTaxType !== usCountryTaxTypes.US2 && NOL === NOLValues.UTILIZE,
    then: yupString().nullable().required(ERROR_MESSAGES.required('State Pre Post NOL')),
    otherwise: yupString().nullable(),
  }),
  NOL: yupString().nullable(),
  recordVA: yupString().nullable(),
  currentTaxRate: yupNumber().required(ERROR_MESSAGES.required('Current Tax Rate')).nullable(),
  deferredTaxRate: yupNumber().nullable(),
  comment: yupString().nullable(),
};

const obligationSchema = yupObject().shape(obligationSchemaShape);

export const checkEntityForDuplicate = (
  obligation,
  _,
  allObligations
) => {
  if (!obligation.meta.isAdded) {
    return {};
  }

  const duplicates = allObligations.filter(entity => (
    entity.companyCode === obligation.companyCode
    && entity.countryCode === obligation.countryCode
    && entity.countryTaxType === obligation.countryTaxType
    && !entity.meta.isAdded
  ));

  if (duplicates.length) {
    const messages = [
      'Obligation for selected Company Code, Country Code and Country Tax Type already exists',
    ];
    return {
      companyCode: messages,
      countryCode: messages,
      countryTaxType: messages,
    };
  }

  return {};
};

const checkForRelatedObligationEnabled = (obligation, context) => {
  const { existingObligations: allObligations } = context;
  if (obligation.enabledFlag === YES_FLAG && obligation.homeOfficeCompanyCode) {
    const foundRelatedObligation = allObligations.some(
      o =>
        o.enabledFlag === YES_FLAG
        && o.countryTaxType === usCountryTaxTypes.US2
        && o.companyCode === obligation.homeOfficeCompanyCode
    );
    return foundRelatedObligation ? {} : {
      homeOfficeCompanyCode: ['US2 Obligation is not Enabled for Home Office Company Code'],
    };
  }
  return {};
};

const hasShareholderOrBranchSecondaryTax = obligation => obligation.secondaryTax === SecondaryTax.Branch
  || obligation.secondaryTax === SecondaryTax.Shareholder;

const isUSStateObligation = obligation => obligation.countryCode === countryCodes.US
  && obligation.countryTaxType !== usCountryTaxTypes.US2;

const isNonUSObligation = obligation => obligation.countryCode !== countryCodes.US;

const checkForNonUSObligationtoSetUSObligation = (obligation, context) => {
  const { existingObligations: allObligations } = context;
  if (!obligation.companyCode) {
    return false;
  }

  const obligationCompanyCode = context.companyCodeOptions.find(({ value }) => value === obligation.companyCode);
  if (obligationCompanyCode.associatedLeCountryCode !== countryCodes.US
        && obligation.countryCode === countryCodes.US
        && !allObligations.some(o => (
          o.companyCode === obligation.companyCode
          && hasShareholderOrBranchSecondaryTax(o)
        ))
  ) {
    return {
      countryTaxType: ['Non US Company Code cannot have US obligation unless secondary tax is set to Shareholder or Branch'],
    };
  }
  return {};
};

const checkForNonUSObligationSecondaryTax = (obligation, context) => {
  if (!obligation.companyCode) {
    return false;
  }
  const obligationCompanyCode = context.companyCodeOptions.find(({ value }) => value === obligation.companyCode);
  if (obligationCompanyCode.associatedLeCountryCode !== countryCodes.US
       && hasShareholderOrBranchSecondaryTax(obligation)
      && obligation.localTaxType === 'FPS') {
    return {
      secondaryTax: ['Non US Obligation cannot have secondary tax as Shareholder or Branch if the Local Tax Type is FPS'],
    };
  }
  return {};
};

const checkForUSObligationtoSetNonUSObligation = (obligation, context) => {
  if (!obligation.companyCode) {
    return false;
  }
  const obligationCompanyCode = context.companyCodeOptions.find(({ value }) => value === obligation.companyCode);
  if (obligationCompanyCode.associatedLeCountryCode
       && obligationCompanyCode.associatedLeCountryCode === countryCodes.US
       && obligation.countryCode !== countryCodes.US) {
    return {
      countryCode: ['Non US Obligation cannot be added for a US Company Code'],
    };
  }
  return {};
};

const checkForNonUSObligationtoSetDifferentCountryCode = (obligation, context) => {
  const { existingObligations: allObligations } = context;
  if (!obligation.companyCode) {
    return false;
  }
  if (obligation.countryCode === countryCodes.US) {
    return false;
  }

  const obligationCompanyCode = context.companyCodeOptions.find(({ value }) => value === obligation.companyCode);
  if (obligationCompanyCode.associatedLeCountryCode !== countryCodes.US) {
    const foundRelatedObligation = allObligations.some(
      o => o.companyCode === obligation.companyCode
        && o.countryCode !== obligation.countryCode
        && o.countryCode !== countryCodes.US
    );
    return foundRelatedObligation ? {
      countryCode: ['Non US Obligation already exist with different Country Code'],
    } : {};
  }
  return {};
};

export const shouldWarnAboutDisablingRelatedUSObligations = (obligation, context, allObligations) => {
  if (obligation.meta.isAdded) {
    return false;
  }
  const existingObligation = allObligations.find(
    existingObligation => areEqualObligations(existingObligation, obligation)
  );
  const obligationCompanyCode = context.companyCodeOptions.find(({ value }) => value === obligation.companyCode);

  return obligationCompanyCode.subledgerFlag === YES_FLAG
    && (
      hasShareholderOrBranchSecondaryTax(existingObligation)
      && obligation.secondaryTax === SecondaryTax.None
    )
    && allObligations.some(({ countryCode, companyCode, enabledFlag, countryTaxType }) =>
      companyCode === obligation.companyCode
      && countryCode === countryCodes.US
      && (countryTaxType === usCountryTaxTypes.US1 || countryTaxType === usCountryTaxTypes.US2)
      && enabledFlag === YES_FLAG
    );
};


export const shouldWarnAboutClearingBSMBalances = (
  obligation, context, allObligations, { isBeingRemoved = false } = {}
) => {
  if (obligation.meta.isAdded) {
    return false;
  }

  const existingObligation = allObligations.find(
    existingObligation => areEqualObligations(existingObligation, obligation)
  );

  const obligationCompanyCode = context.companyCodeOptions.find(({ value }) => value === obligation.companyCode);

  const isBeingDisabledOrRemoved = isBeingRemoved
    || (
      obligation.enabledFlag === NO_FLAG
      && existingObligation.enabledFlag === YES_FLAG
    );

  return isBeingDisabledOrRemoved
    && obligationCompanyCode.subledgerFlag === YES_FLAG
    && isUSStateObligation(obligation)
    && allObligations.some(existingObligation =>
      existingObligation.companyCode === obligation.companyCode
      && isNonUSObligation(existingObligation)
      && hasShareholderOrBranchSecondaryTax(existingObligation)
    );
};

const getWarningMessages = (obligation, context, allObligations) => ({
  ...shouldWarnAboutClearingBSMBalances(obligation, context, allObligations)
    ? {
      enabledFlag: ['You are disabling obligations for a BSM enabled company code. This may orphan balances. Only click Save if you confirm there are no existing BSM Balances, or they have been cleared.'],
    }
    : {},
  ...shouldWarnAboutDisablingRelatedUSObligations(obligation, context, allObligations)
    ? {
      secondaryTax: ['You are clearing Secondary Tax for BSM enabled company code: All US obligations will be disabled'],
    }
    : {},
});


export const validateObligation = (obligation, context, allObligations) => {
  const baseSchemaError = validateBaseSchema(obligationSchema, obligation, context);
  const duplicateError = checkEntityForDuplicate(obligation, context, allObligations);
  const obligationEnabledError = checkForRelatedObligationEnabled(obligation, context);
  const usObligationEnabledError = checkForNonUSObligationtoSetUSObligation(obligation, context);
  const secondaryTaxTypeError = checkForNonUSObligationSecondaryTax(obligation, context);
  const nonUSObligationSetError = checkForUSObligationtoSetNonUSObligation(obligation, context);
  const nonUSCountryCodeSetEror = checkForNonUSObligationtoSetDifferentCountryCode(obligation, context);

  return {
    errors: merge({}, obligationEnabledError, baseSchemaError, duplicateError, usObligationEnabledError,
      secondaryTaxTypeError, nonUSObligationSetError, nonUSCountryCodeSetEror),
    warnings: getWarningMessages(obligation, context, allObligations),
  };
};
